import type {
  ChatAnswerResponse,
  ChatMessage,
  FeedbackPayload,
  ProductContextItem,
} from "../types/api";
import type { KeyboardEvent, ReactNode } from "react";
import { useLayoutEffect, useMemo, useRef, useState } from "react";

import { streamChat, submitFeedback, trackLinkClick } from "../api/chatClient";
import "../styles/widget.css";

export interface WidgetShellProps {
  apiBaseUrl: string;
  initialMessages?: ChatMessage[];
  onAnswer?: (answer: ChatAnswerResponse) => void;
  embedded?: boolean;
  title?: string;
}

const starterMessage: ChatMessage = {
  role: "assistant",
  content: "Ask me about Tecmojo products, stock, specs, SKUs, or alternatives.",
};

const MD_LINK_RE = /\[([^\]]*)\]\((https?:\/\/[^)\s]+)\)/g;
const BARE_URL_RE = /(https?:\/\/[^\s<>"']+)/g;

function plainSegmentToNodes(
  segment: string,
  nextKey: () => number,
  onLinkClick?: (url: string, label: string) => void,
): ReactNode[] {
  if (!segment) {
    return [];
  }
  const parts = segment.split(BARE_URL_RE);
  const out: ReactNode[] = [];
  parts.forEach((part, index) => {
    if (index % 2 === 1) {
      let label = "Open link";
      try {
        label = new URL(part).hostname.replace(/^www\./, "");
      } catch {
        /* ignore */
      }
      out.push(
        <a
          key={String(nextKey())}
          href={part}
          target="_blank"
          rel="noreferrer"
          onClick={() => onLinkClick?.(part, label)}
        >
          {label}
        </a>,
      );
    } else if (part) {
      out.push(<span key={String(nextKey())}>{part}</span>);
    }
  });
  return out;
}

/** Renders [View product](https://...) as a labeled link; bare URLs use hostname as link text. */
function renderChatMessageContent(
  text: string,
  onLinkClick?: (url: string, label: string) => void,
): ReactNode {
  if (!text) {
    return null;
  }
  let keyCounter = 0;
  const nextKey = () => keyCounter++;
  const nodes: ReactNode[] = [];
  let last = 0;
  const re = new RegExp(MD_LINK_RE.source, "g");
  let match: RegExpExecArray | null;
  while ((match = re.exec(text)) !== null) {
    if (match.index > last) {
      nodes.push(
        ...plainSegmentToNodes(text.slice(last, match.index), nextKey, onLinkClick),
      );
    }
    const label = (match[1] || "Open link").trim() || "Open link";
    const href = match[2];
    nodes.push(
      <a
        key={String(nextKey())}
        href={href}
        target="_blank"
        rel="noreferrer"
        onClick={() => onLinkClick?.(href, label)}
      >
        {label}
      </a>,
    );
    last = re.lastIndex;
  }
  if (last < text.length) {
    nodes.push(...plainSegmentToNodes(text.slice(last), nextKey, onLinkClick));
  }
  return nodes.length === 0 ? null : <>{nodes}</>;
}

export function WidgetShell(props: WidgetShellProps) {
  const [messages, setMessages] = useState<ChatMessage[]>(
    props.initialMessages?.length ? props.initialMessages : [starterMessage],
  );
  const [draft, setDraft] = useState("");
  const [status, setStatus] = useState("");
  const [meta, setMeta] = useState<ChatAnswerResponse | null>(null);
  const [isOpen, setIsOpen] = useState(!props.embedded);
  const pendingIndexRef = useRef<number | null>(null);
  const chatLogEndRef = useRef<HTMLDivElement>(null);
  const productContextRef = useRef<ProductContextItem[]>([]);
  const requestIdRef = useRef<string>("");
  const [feedbackStatus, setFeedbackStatus] = useState("");
  const [replyComplete, setReplyComplete] = useState(false);
  const showFeedback = Boolean(meta?.request_id) && replyComplete;

  const canSend = useMemo(() => draft.trim().length > 0, [draft]);
  const title = props.title ?? "Ask Tecmojo";

  async function handleSend() {
    const content = draft.trim();
    if (!content) {
      return;
    }
    const nextMessages = [
      ...messages,
      { role: "user", content } as ChatMessage,
      { role: "assistant", content: "" } as ChatMessage,
    ];
    pendingIndexRef.current = nextMessages.length - 1;
    setMessages(nextMessages);
    setDraft("");
    setStatus("Streaming response...");
    setMeta(null);
    setReplyComplete(false);

    try {
      await streamChat(
        props.apiBaseUrl,
        nextMessages.filter((message) => message.content.trim()),
        (delta) => {
          setMessages((current) => {
            const copy = [...current];
            const idx = pendingIndexRef.current;
            if (idx === null || !copy[idx]) {
              return current;
            }
            copy[idx] = {
              ...copy[idx],
              content: `${copy[idx].content}${delta}`,
            };
            return copy;
          });
        },
        (payload) => {
          productContextRef.current = payload.product_context ?? [];
          requestIdRef.current = payload.request_id ?? "";
          setMeta(payload);
          props.onAnswer?.(payload);
        },
        5,
        productContextRef.current,
      );
      setStatus("");
      setReplyComplete(true);
    } catch (error) {
      const message =
        error instanceof Error ? error.message : "Chat request failed.";
      setStatus("Error");
      setReplyComplete(false);
      setMessages((current) => {
        const copy = [...current];
        const idx = pendingIndexRef.current;
        if (idx === null || !copy[idx]) {
          return current;
        }
        copy[idx] = { ...copy[idx], content: message };
        return copy;
      });
    }
  }

  useLayoutEffect(() => {
    if (props.embedded && !isOpen) {
      return;
    }
    chatLogEndRef.current?.scrollIntoView({ block: "end", inline: "nearest" });
  }, [messages, isOpen, props.embedded]);

  const chatLog = (
    <div className="chat-log">
      {messages.map((message, index) => (
        <div key={`${message.role}-${index}`} className={`message ${message.role}`}>
          {renderChatMessageContent(message.content, (url, label) => {
            const requestId = requestIdRef.current;
            if (!requestId) {
              return;
            }
            void trackLinkClick(props.apiBaseUrl, {
              request_id: requestId,
              url,
              title: label,
              message_id: `msg-${index}`,
            }).catch(() => {});
          })}
        </div>
      ))}
      <div className="chat-log-end" ref={chatLogEndRef} aria-hidden />
    </div>
  );

  async function sendFeedback(helpful: boolean) {
    const requestId = requestIdRef.current;
    if (!requestId) {
      setFeedbackStatus("No response ID yet.");
      return;
    }
    const lastAssistant = [...messages]
      .reverse()
      .find((message) => message.role === "assistant" && message.content.trim());
    const lastUser = [...messages]
      .reverse()
      .find((message) => message.role === "user" && message.content.trim());
    const payload: FeedbackPayload = {
      request_id: requestId,
      helpful,
      meta: {
        llm_answer: lastAssistant?.content.trim() ?? "",
        grounded_answer: meta?.grounded_answer?.trim() ?? "",
        user_question: lastUser?.content.trim() ?? "",
      },
    };
    try {
      await submitFeedback(props.apiBaseUrl, payload);
      setFeedbackStatus(helpful ? "Thanks for the positive feedback." : "Thanks, we will improve this.");
    } catch {
      setFeedbackStatus("Could not send feedback.");
    }
  }

  function handleEmbedKeyDown(event: KeyboardEvent<HTMLTextAreaElement>) {
    if (event.key !== "Enter" || event.shiftKey) {
      return;
    }
    event.preventDefault();
    void handleSend();
  }

  const composer = props.embedded ? (
    <div className="composer widget-composer">
      <div className="widget-composer-field">
        <textarea
          value={draft}
          onChange={(event) => setDraft(event.target.value)}
          onKeyDown={handleEmbedKeyDown}
          placeholder="Ask about products, dimensions, stock, or alternatives..."
          rows={3}
          aria-label="Message"
        />
        <button
          type="button"
          className="widget-composer-send"
          onClick={() => void handleSend()}
          disabled={!canSend}
          aria-label="Send message"
        >
          <svg
            className="widget-composer-send-icon"
            width="20"
            height="20"
            viewBox="0 0 24 24"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
            aria-hidden
          >
            <path
              d="M22 2 11 13"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
            <path
              d="M22 2 15 22l-4-9-9-4L22 2Z"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </button>
      </div>
    </div>
  ) : (
    <div className="composer">
      <textarea
        value={draft}
        onChange={(event) => setDraft(event.target.value)}
        placeholder="Ask about products, dimensions, stock, or alternatives..."
      />
      <div className="button-stack">
        <button type="button" className="button-primary" onClick={handleSend} disabled={!canSend}>
          Send
        </button>
      </div>
    </div>
  );

  if (props.embedded) {
    return (
      <div className="widget-root widget-root-embedded">
        <div className="floating-widget">
          {isOpen ? (
            <section className="widget-panel">
              <div className="widget-header">
                <div>
                  <h2>{title}</h2>
                </div>
                <button
                  type="button"
                  className="widget-icon-button"
                  onClick={() => setIsOpen(false)}
                  aria-label="Close chat widget"
                >
                  x
                </button>
              </div>
              <div className="chat-shell widget-embed-chat-shell">
                {chatLog}
                {showFeedback ? (
                  <div className="feedback-row">
                    <span className="muted">Was this answer helpful?</span>
                    <button type="button" className="feedback-btn" onClick={() => void sendFeedback(true)}>
                      Yes
                    </button>
                    <button type="button" className="feedback-btn" onClick={() => void sendFeedback(false)}>
                      No
                    </button>
                  </div>
                ) : null}
                {showFeedback && feedbackStatus ? <div className="muted">{feedbackStatus}</div> : null}
                <div className="widget-embed-footer">
                  {status ? <div className="status widget-embed-status">{status}</div> : null}
                  {composer}
                </div>
              </div>
            </section>
          ) : null}
          {!isOpen ? (
            <button
              type="button"
              className="widget-launcher"
              onClick={() => setIsOpen(true)}
              aria-label="Open chat widget"
            >
              {title}
            </button>
          ) : null}
        </div>
      </div>
    );
  }

  return (
    <div className="app-shell">
      <div className="app-layout">
        <section className="panel">
          <div className="chat-shell">
            <div>
              <h1>Tecmojo Chatbot Widget</h1>
              <p className="muted">
                Test the embeddable widget against the live RAG API.
              </p>
            </div>
            {chatLog}
            {showFeedback ? (
              <div className="feedback-row">
                <span className="muted">Was this answer helpful?</span>
                <button type="button" className="feedback-btn" onClick={() => void sendFeedback(true)}>
                  Yes
                </button>
                <button type="button" className="feedback-btn" onClick={() => void sendFeedback(false)}>
                  No
                </button>
              </div>
            ) : null}
            {showFeedback && feedbackStatus ? <div className="muted">{feedbackStatus}</div> : null}
            {composer}
            {status ? <div className="status">{status}</div> : null}
          </div>
        </section>

        <aside className="panel">
          <h3>Alternatives</h3>
          <div className="alt-list">
            {(meta?.alternatives ?? []).map((item) => (
              <div key={item.identifier} className="alt-card">
                <div>{item.name}</div>
                <div className="muted">
                  {item.sku} · {item.price} {item.currency}
                </div>
                <a href={item.url} target="_blank" rel="noreferrer">
                  Open product
                </a>
              </div>
            ))}
          </div>

          <h3>Grounded Summary</h3>
          <div className="debug-block">{meta?.grounded_answer ?? ""}</div>

          <h3>Debug</h3>
          <div className="debug-block">
            {JSON.stringify(
              meta ? { query: meta.query, chat_query: meta.chat_query } : {},
              null,
              2,
            )}
          </div>
        </aside>
      </div>
    </div>
  );
}
